"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { useQueryParams } from "apps/website/hooks/useQueryParams";

import { getNextPageSlug } from "../navigation/getNextPageSlug";
import { getPreviousPageSlug } from "../navigation/getPreviousPageSlug";

import { useFlowNavigationContext } from "./useFlowNavigationContext";
import { FlowPage } from "../../../shared/constants";

export interface FlowNavigation<T extends string = never> {
  goToNext: () => void;
  goBack: () => void;
  canContinue: boolean;
  canGoBack: boolean;
  nextPageSlug: T | null;
}

export function useFlowNavigation<T extends string = never>(
  currentSlug: string,
  flowPages: FlowPage<T>[]): FlowNavigation<T> {
  const router = useRouter();
  const context = useFlowNavigationContext();
  const { chainQueryParamsToUrl } = useQueryParams();

  const nextPageSlug = getNextPageSlug<T>(currentSlug, context, flowPages);
  const previousPageSlug = getPreviousPageSlug<T>(currentSlug, context, flowPages);

  const canContinue = nextPageSlug !== null;
  const canGoBack = previousPageSlug !== null;

  const goToNext = useCallback(() => {
    if (canContinue && nextPageSlug) {
      const url = chainQueryParamsToUrl(nextPageSlug);
      router.push(url);
    }
  }, [ canContinue, nextPageSlug, router, chainQueryParamsToUrl ]);

  const goBack = useCallback(() => {
    if (canGoBack && previousPageSlug) {
      const url = chainQueryParamsToUrl(previousPageSlug);
      router.push(url);
    }
  }, [ canGoBack, previousPageSlug, router, chainQueryParamsToUrl ]);

  return {
    goToNext,
    goBack,
    canContinue,
    canGoBack,
    nextPageSlug,
  };
}
